import axios from 'axios'
import {Message, MessageBox} from 'element-ui'
import store from '../store'
import {getToken} from '@/utils/auth'
import i18n from "@/lang";

// 防止多个接口同时返回 401 时重复弹窗。
let isHandlingUnauthorized = false
let unauthorizedTask = null

const handleUnauthorized = () => {
  if (!isHandlingUnauthorized) {
    isHandlingUnauthorized = true
    const confirmation = getToken()
      ? MessageBox.confirm(i18n.t('system.loggedOut'), i18n.t('system.confirmLogout'), {
        confirmButtonText: i18n.t('button.confirm'),
        type: 'warning',
        showCancelButton: false,
        showClose: false,
        closeOnClickModal: false,
        closeOnPressEscape: false
      })
      : Promise.resolve()

    // 凭证已经失效，直接清理本地状态，避免退出接口再次返回 401。
    unauthorizedTask = confirmation.catch(() => {})
      .then(() => store.dispatch('LogOut', {localOnly: true}))
      .finally(() => {
        // 当前页不会收到自身的 storage 事件，必须主动跳转。
        window.location.hash = '#/login'
        isHandlingUnauthorized = false
        unauthorizedTask = null
      })
  }
  // 等待退出完成后再通知路由守卫失败。
  // return unauthorizedTask.then(() => {
  //   throw new Error('Unauthorized')
  // })
}

const addInterceptors = (instance) => {
  // request拦截器
  instance.interceptors.request.use(config => {
    // 请求头携带当前国际化语言
    config.headers['Accept-Language'] = localStorage.getItem('language-seller') === 'zh' ? 'zh-CN' : 'en'

    if (store.getters.token) {
      config.headers['Authorization'] = getToken() // 让每个请求携带自定义token 请根据实际情况自行修改
    }
    return config
  }, error => {
    Promise.reject(error)
  })

  // response拦截器
  instance.interceptors.response.use(
    response => {
      let res = response.data
      const silentCodes = Array.isArray(response.config && response.config.silentCodes)
        ? response.config.silentCodes
        : []

      if (response.config && response.config.responseType === 'blob') {
        return response
      }

      // application/octet-stream文件流
      if (response.headers['content-type'] === 'application/octet-stream'
        || response.headers['content-type'] === 'application/octet-stream;charset=UTF-8'
        || response.headers['content-type'] === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
      ) {
        return response
      }
      // 401:未登录/token过期
      if (res.code === 401) {
        return handleUnauthorized()
      } else if (res.code === 301) {
        Message({
          message: res.message,
          type: 'warning',
          duration: 5 * 1000
        })
        return response.data
      } else if (res.code === 500) {
        Message({
          message: res.message,
          type: 'warning',
          duration: 5 * 1000
        })
        return Promise.reject(new Error(res.message))
      } else if (res.code !== 200 && res.code !== 7509) {
        if (silentCodes.includes(res.code)) {
          return response.data
        }
        const message = res.message
        if (!message) {
          Message({
            message: i18n.t('message.systemError'),
            type: 'warning',
            duration: 3 * 1000
          })
        } else {
          Message({
            message: message,
            type: 'warning',
            duration: 3 * 1000
          })
          return response.data
        }
      } else {
        return response.data
      }
    },
    error => {
      if (error.response && error.response.status === 401) {
        return handleUnauthorized()
      }
      // ========== 场景B：HTTP状态码429（网关限流）==========
      if (error.response && error.response.status === 429) {
        const resData = error.response.data || {}
        const msg = resData.message
        Message({
          message: msg,
          type: 'warning',
          duration: 3 * 1000
        })
      } else {
        Message({
          message: error.message,
          type: 'error',
          duration: 3 * 1000
        })
      }
      return Promise.reject(error)
    })
}

export const userService = 'srs-user-service'
export const productService = 'srs-product-service'
export const orderService = 'srs-order-service'
export const baseService = 'srs-base-data-service'
export const thirdPartService = 'srs-thirdparty-service'

export const userUrl = process.env.BASE_API + 'srs-user-service'
const productUrl = process.env.BASE_API + 'srs-product-service'
export const orderUrl = process.env.BASE_API + 'srs-order-service'
const baseUrl = process.env.BASE_API + 'srs-base-data-service'
export const thirdPartUrl = process.env.BASE_API + 'srs-thirdparty-service'
export const messageUrl = process.env.BASE_API + 'srs-message-service'
export const paymentUrl = process.env.BASE_API + 'srs-payment-service'
export const thirdPartyShopUrl = process.env.BASE_API + 'srs-thirdpartyshop-service'


// 创建axios实例
const requestUser = axios.create({
  baseURL: userUrl,
  timeout: 30000
})

const requestProduct = axios.create({
  baseURL: productUrl,
  timeout: 30000
})

const requestOrder = axios.create({
  baseURL: orderUrl, //orderUrl "http://172.16.20.21:9504/"
  timeout: 30000
})

const requestOrderFile = axios.create({
  baseURL: orderUrl, //orderUrl "http://172.16.20.21:9504/"
  timeout: 30000,
  headers: {
    'X-Requested-With': 'XMLHttpRequest'
  }
})

const requestBase = axios.create({
  baseURL: baseUrl,
  timeout: 30000
})

const requestThirdPart = axios.create({
  baseURL: thirdPartUrl,
  timeout: 30000
})

const requestMessage = axios.create({
  baseURL: messageUrl, // API
  timeout: 30000
});

const requestPayment = axios.create({
  baseURL: paymentUrl, // API
  timeout: 30000
});

const requestThirdPartyShop = axios.create({
  baseURL: thirdPartyShopUrl, // API
  timeout: 30000
});

addInterceptors(requestUser)
addInterceptors(requestProduct)
addInterceptors(requestOrder)
addInterceptors(requestOrderFile)
addInterceptors(requestBase)
addInterceptors(requestThirdPart)
addInterceptors(requestMessage)
addInterceptors(requestPayment)
addInterceptors(requestThirdPartyShop)

export {
  requestUser,
  requestProduct,
  requestOrder,
  requestOrderFile,
  requestBase,
  requestThirdPart,
  requestMessage,
  requestPayment,
  requestThirdPartyShop
}



// WEBPACK FOOTER //
// ./src/utils/request.js